import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import useProviderArgs from "./useProviderArgs.js";
import { isObject, isString } from "lodash";
import fetch from "./fetch.js";
export function useProvider() {
  const {
    provider,
    customOptions,
    dependencies,
    requestInit
  } = useProviderArgs(...arguments);
  const {
    onError,
    transform,
    suspense,
    cache,
    ...defaults
  } = customOptions;
  const [loading, setLoading] = useState(defaults.loading);
  const suspenseStatus = useRef("pending");
  const suspender = useRef();
  const mounted = useRef(false);
  const error = useRef();
  const response = useRef();
  const data = useRef(defaults.data);
  const forceUpdate = useReducer(() => ({}), [])[1];
  const doFetch = useCallback(async (provider, providerArgs) => {
    try {
      error.current = undefined;
      if (!suspense) setLoading(true);
      const newRes = await fetch(provider, cache, providerArgs);
      response.current = newRes;
      data.current = transform(data.current, newRes);
    } catch (e) {
      error.current = e;
      data.current = undefined;
    }
    if (!suspense) setLoading(false);
    if (error.current) onError(error.current);
    return data.current;
  }, [provider, customOptions, dependencies, requestInit, suspense, transform, defaults.data, onError, cache]);
  const makeFetch = useCallback(async (providerOrBody, args) => {
    let providerStr = provider;
    let providerArgs = [];
    if (isString(providerOrBody)) {
      providerStr = providerOrBody;
    }
    if (isObject(providerOrBody)) {
      providerArgs = providerOrBody;
    } else if (isObject(args)) {
      providerArgs = args;
    }
    if (suspense) {
      return (async () => {
        suspender.current = doFetch(providerStr, providerArgs).then(newData => {
          suspenseStatus.current = "success";
          return newData;
        }, error => {
          /* istanbul ignore next */
          suspenseStatus.current = "error";
          error.current = error;
          return error;
        });
        forceUpdate();
        return await suspender.current;
      })();
    }
    return doFetch(providerStr, providerArgs);
  }, [doFetch]);
  const request = useMemo(() => Object.defineProperties({
    query: makeFetch
  }, {
    loading: {
      get() {
        return loading;
      }
    },
    data: {
      get() {
        return data.current;
      }
    },
    error: {
      get() {
        return error.current;
      }
    }
  }), [makeFetch]);

  // onMount/onUpdate
  useEffect(() => {
    mounted.current = true;
    if (Array.isArray(dependencies) && provider) {
      request.query(provider, requestInit.args);
    }
    return () => mounted.current = false;
  }, dependencies);
  if (suspense && suspender.current) {
    switch (suspenseStatus.current) {
      case "pending":
        throw suspender.current;
      /* istanbul ignore next */
      case "error":
        throw error.current;
    }
  }
  return Object.assign([request, response.current, loading, error.current], {
    request,
    ...request,
    response: response.current,
    data: data.current,
    loading,
    error: error.current
  });
}
//# sourceMappingURL=useProvider.js.map